import { Fragment, useEffect, useRef, useState } from 'react';
import { ApiFailure, api, apiUpload } from '../lib/api';
import { AddressSearchBox } from './AddressSearchBox';
import { ContactPicker } from './ContactPicker';
import { OrderLinkPicker } from './OrderLinkPicker';
import { Snowflake, X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

interface PlanRow {
  chainNo: string;
  buyer: string;
  totalAmount: string;
  sheet: { name: string; phone: string; address: string };
  matched: { id: string; name: string; phone: string; address: string; lat?: number | null; lng?: number | null } | null;
  matchedBy: 'PHONE' | 'PHONE_NAME' | 'PHONE_ADDRESS' | 'NAME' | 'NAME_ADDRESS' | null;
  use: { name: string; phone: string; address: string; lat?: number | null; lng?: number | null } | null;
  note: string;
  frozen: boolean;
  action: 'CREATE' | 'APPEND_NOTE' | 'FAIL';
  appendTo: string | null;
  appendToRowNo: number | null;
  appendToOrderNo: string | null;
  rowNo: number;
  failReason: string | null;
  /** 分不清是哪个联系人时的候选,按地址像不像排好 */
  candidates: Array<{ id: string; name: string; phone: string; address: string; lat?: number | null; lng?: number | null }> | null;
}

interface Preview {
  batchId: string;
  label: string;
  expectedDeliveryDate: string;
  headerRowNo: number;
  mergedRows: number;
  skippedByStatus: Record<string, number>;
  counts: { total: number; create: number; appendNote: number; fail: number };
  rows: PlanRow[];
}

interface Result {
  created: number;
  appended: number;
  skipped: number;
  /** 人手工改过、改完真的下了单的条数 */
  edited: number;
  /** 挑了联系人之后救回来的条数 */
  picked: number;
  /** 手动关联到已有单、真的加上备注的条数 */
  linked: number;
  /** 手动并到本批另一个接龙号、真的加上备注的条数 */
  merged: number;
  failed: Array<{ chainNo: string; buyer: string; reason: string }>;
}

/** 人挑好的联系人 —— 候选里挑的,或者「关联联系人」搜出来的 */
type PickedContact = NonNullable<PlanRow['candidates']>[number];

/** 人在核对页上改过的三个框。空串 = 这个框没动过 */
interface RowEdit {
  name?: string;
  phone?: string;
  address?: string;
  /** 地址搜出来的坐标 —— 不带的话建出来的单没坐标,会落进复核队列 */
  lat?: number;
  lng?: number;
}

const MATCH_LABEL: Record<string, string> = {
  PHONE: '按电话',
  PHONE_NAME: '电话+名字',
  PHONE_ADDRESS: '电话+地址最像的',
  /*
   * ★ 「按名字」要和按电话的区分开、而且看起来就该更可疑:
   *   电话是唯一的,名字不是。这两条是电话一个都没对上时的退路,
   *   核对的人看到它应该多看一眼。
   */
  NAME: '⚠ 只按名字(电话没对上)',
  NAME_ADDRESS: '⚠ 名字+地址最像的(电话没对上)',
};

/**
 * 团购接龙导入。
 *
 * ★ 和「导入订单」分开一个入口。两者的表不是一个东西:
 *   那边是商家发来的规整模板(可以选列映射),这边是接龙小程序导出的原表 ——
 *   标题占第一行、一行一个商品、地址是客户自己在微信里填的。
 *   混在一个对话框里的话,两套完全不同的字段会挤在一起,
 *   而选错模式的人拿到的报错会指向另一条路上的规则。
 *
 * ★ 预览这一屏的重点是**把「表里写的」和「我们要用的」并排摆出来**。
 *   这条路会拿库里的联系人去纠正表里写歪的收货人/电话/地址,
 *   而纠正得对不对只有人能判断 —— 不摆出来的话,一次错配就是
 *   把货送到另一个人家里,而那一单看起来一切正常。
 */
export function ChainImportDialog({
  points,
  onClose,
  onDone,
}: {
  points: Array<{ id: string; name: string }>;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');
  const [day, setDay] = useState('');
  const [pointId, setPointId] = useState(() => (points.length === 1 ? points[0]!.id : ''));
  const pointTouched = useRef(false);

  useEffect(() => {
    if (!pointTouched.current && points.length === 1) {
      setPointId(points[0]!.id);
    }
  }, [points]);

  const [preview, setPreview] = useState<Preview | null>(null);
  /**
   * 被手动划掉的行(Excel 行号)。
   *
   * ★ 为什么要有这个:这条路会拿库里的联系人改写收货人 / 电话 / 地址,
   *   而改得对不对只有人能判断。看到一条匹配得不对(比如匹配到的联系人
   *   是个店名),他要能当场划掉,而不是「先导进去再去取消那一单」——
   *   后者那一单可能已经派出去了。
   */
  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  /** 整批都是冻品 —— 只是给核对页定个默认值 */
  const [allFrozen, setAllFrozen] = useState(false);
  /**
   * 核对页上勾了冻品的那几行。
   *
   * ★ 单独一份状态,而不是直接改 `preview.rows[i].frozen`:
   *   预览那份是服务端算的默认值,人改过之后两者要能分得开 ——
   *   确认时发的是**人最后勾的**这一份。
   */
  const [frozen, setFrozen] = useState<Set<number>>(new Set());
  /**
   * 人手工改过的那几行:行号 → 改过的三个框。
   *
   * ★ 单独一份,不直接改 `preview.rows` —— 预览那份是服务端算的,
   *   要和人改的分得开:确认时发过去的是这一份,而「表里写的」那一列
   *   仍然显示原样,好让他对照自己改了什么。
   *
   * ★ 只存**他真的动过**的框(空串 = 没动),所以服务端那边
   *   没动的两个框会沿用这一行原来的值。
   */
  const [edits, setEdits] = useState<Map<number, RowEdit>>(new Map());
  /** 正在展开编辑的那一行 —— 一次只开一行,表格已经很宽了 */
  const [editing, setEditing] = useState<number | null>(null);
  /**
   * 人挑好的联系人:行号 → 联系人 id。
   *
   * ★ 和 `edits` 分开存。挑联系人是「这几个里就是他」,手填是
   *   「都不是,我自己来」—— 后端对两者的处理也不一样(挑的要重新查
   *   那个人今天有没有单,手填的一律新建)。
   */
  const [picks, setPicks] = useState<Map<number, PickedContact>>(new Map());
  /** 正在为哪一行搜联系人 */
  const [contactLinking, setContactLinking] = useState<number | null>(null);
  /**
   * 人手动并到本批另一行的:行号 → 目标行号(关联接龙号)。
   *
   * ★ 同一个接龙里一个人下了好几单、信息又填得不一样(电话差一位、
   *   地址一个写全一个写简)—— 自动那条路认不出来,他看接龙号就知道。
   *   并过去之后这一行不建单,备注加到那一行的单上。
   */
  const [merges, setMerges] = useState<Map<number, number>>(new Map());
  /** 正在为哪一行挑接龙号 */
  const [merging, setMerging] = useState<number | null>(null);
  /**
   * 人手动关联到已有单的行:行号 → 订单号。
   *
   * ★ 为什么需要:自动那条路按**规整后的手机号**找「这个人今天有没有单」,
   *   而表里那个号和已有单上那个常常不是同一种写法、甚至是另一个号
   *   (家里人下的单、换过号)—— 于是明明有单却判成「建单」,
   *   导完就是同一个人同一天两张单,司机跑两趟。
   *   他看一眼就知道「这是上午那一单的人」。
   */
  const [links, setLinks] = useState<Map<number, string>>(new Map());
  /** 正在为哪一行找已有的单 */
  const [linking, setLinking] = useState<number | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function doPreview() {
    if (!file || !label.trim() || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('label', label.trim());
      if (day) fd.append('expectedDeliveryDate', day);
      if (allFrozen) fd.append('allFrozen', 'true');
      if (pointId) fd.append('pickupPointId', pointId);
      const p = await apiUpload<Preview>('/imports/chain/preview', fd);
      setPreview(p);
      setSkipped(new Set());
      setEdits(new Map());
      setPicks(new Map());
      setLinks(new Map());
      setLinking(null);
      setMerges(new Map());
      setMerging(null);
      setContactLinking(null);
      setEditing(null);
      // 默认值来自服务端(整批勾了「全是冻品」就都是 true),之后由人改
      setFrozen(new Set(p.rows.filter((r) => r.frozen).map((r) => r.rowNo)));
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '读不出这个文件');
    } finally {
      setBusy(false);
    }
  }

  /**
   * 点下确认之后**会建单**的行数。
   *
   * ★ 人手工改过的行一律算进来:改过的一律按新建一单走(见后端 chain-edit),
   *   哪怕它原来是「下不了」。
   *   顺便:改过之后就不再是「加到已有单上」了,所以从 append 那边排除掉。
   */
  const willCreate = preview
    ? preview.rows.filter(
        (r) =>
          !skipped.has(r.rowNo) &&
          !links.has(r.rowNo) &&
          !merges.has(r.rowNo) &&
          (edits.has(r.rowNo) || picks.has(r.rowNo) || r.action === 'CREATE'),
      ).length
    : 0;
  const willAppend = preview
    ? preview.rows.filter(
        (r) =>
          !skipped.has(r.rowNo) &&
          (links.has(r.rowNo) ||
            merges.has(r.rowNo) ||
            (!edits.has(r.rowNo) && !picks.has(r.rowNo) && r.action === 'APPEND_NOTE')),
      ).length
    : 0;

  /** 这一行点确认之后会不会真的用上(建单或加备注)—— 决定给不给勾选框 */
  const usable = (r: PlanRow) =>
    r.action !== 'FAIL' ||
    picks.has(r.rowNo) ||
    edits.has(r.rowNo) ||
    merges.has(r.rowNo) ||
    links.has(r.rowNo);

  /** 撤掉某一行的一种手动处理 */
  const dropManual = (rowNo: number, which: 'pick' | 'link' | 'merge') => {
    const set = { pick: setPicks, link: setLinks, merge: setMerges }[which] as (
      f: (m: Map<number, unknown>) => Map<number, unknown>,
    ) => void;
    set((prev) => {
      if (!prev.has(rowNo)) return prev;
      const next = new Map(prev);
      next.delete(rowNo);
      return next;
    });
  };

  /** 一行的手动处理互斥:点了其中一样,另外几样就作废,免得他以为几样同时生效 */
  const clearManual = (rowNo: number, keep: 'pick' | 'link' | 'merge') => {
    for (const w of ['pick', 'link', 'merge'] as const) if (w !== keep) dropManual(rowNo, w);
  };

  /**
   * 能并过去的目标行:本批里**自己会建单**(或加到一张已有单上)的行。
   *
   * ★ 自己就是并到别人身上的那些不给选 —— 选了的话要顺着链再找一层,
   *   他看到的「并到接龙 5」实际落在接龙 2 的单上,对不上号。
   */
  const mergeTargets = (r: PlanRow) =>
    preview
      ? preview.rows.filter(
          (t) =>
            t.rowNo !== r.rowNo &&
            !skipped.has(t.rowNo) &&
            !merges.has(t.rowNo) &&
            usable(t) &&
            (links.has(t.rowNo) ||
              picks.has(t.rowNo) ||
              edits.has(t.rowNo) ||
              t.appendToRowNo == null),
        )
      : [];

  /**
   * 「怎么处理」那一块:状态、候选、关联、改。
   *
   * ★ 抽成函数是因为它要在**两个地方**长一样:桌面的表格单元格、
   *   手机上的卡片。各写一份的话,以后加一个按钮必然只加到一边,
   *   而漏掉的那一边是手机 —— 没人会在手机上点着检查。
   */
  const decision = (r: PlanRow, scope: 'desktop' | 'mobile' = 'desktop') => (
    <>
      {skipped.has(r.rowNo) ? (
        <span className="text-slate-500">手动不使用 —— 会列进失败清单;勾上「用」才能改、才会导</span>
      ) : merges.has(r.rowNo) ? (
        <>
          <span className="text-blue-700 dark:text-blue-400">
            并到接龙
            {preview?.rows.find((t) => t.rowNo === merges.get(r.rowNo))?.chainNo}的单上
            <span className="block text-slate-400">不单独建单,备注加一行:{r.note}</span>
          </span>
          <button
            onClick={() => dropManual(r.rowNo, 'merge')}
            className="mt-1 text-slate-500 underline"
          >
            取消关联接龙号
          </button>
        </>
      ) : (
        <>
          {links.has(r.rowNo) && (
            <span className="text-blue-700 dark:text-blue-400">
              不单独建单,备注加到关联的单 {links.get(r.rowNo)} 上
            </span>
          )}
          {r.action === 'CREATE' && !picks.has(r.rowNo) && !links.has(r.rowNo) && (
            <span className="text-green-700 dark:text-green-400">建单</span>
          )}
          {r.action === 'APPEND_NOTE' && !picks.has(r.rowNo) && !links.has(r.rowNo) && (
            <span className="text-blue-700 dark:text-blue-400">
              加到已有的单上
              <span className="block text-slate-400">{r.appendTo}</span>
            </span>
          )}
          {r.action === 'FAIL' && !picks.has(r.rowNo) && !links.has(r.rowNo) && (
            <span className="text-amber-700 dark:text-amber-400">下不了 —— {r.failReason}</span>
          )}
          {picks.has(r.rowNo) && (
            <span className="font-medium text-green-700 dark:text-green-400">
              已关联联系人「{picks.get(r.rowNo)!.name}」(这个人今天已有单就加备注,没有就建单)
            </span>
          )}
          {/*
            ★ 分不清是哪个联系人时,把候选**摆出来让他挑**。
              机器分不出来不等于人分不出来 —— 他认得「这是王姐家、那是她店里」。
              在这之前这种行只能进失败清单,然后他回建单页把那个人重新录一遍,
              而答案此刻就在屏幕上。

            ★ 不预选任何一个。预选了他会直接确认 ——
              而「预选的那个」正是机器刚刚承认自己挑不出来的。
          */}
          {r.candidates && r.candidates.length > 0 && (
            <div className="mt-1 space-y-1">
              <div className="text-slate-500">这几个里是哪一位?</div>
              {r.candidates.map((c) => {
                const isSelected = picks.get(r.rowNo)?.id === c.id;
                return (
                  <label
                    key={c.id}
                    className={`flex cursor-pointer items-start gap-2 rounded-lg border p-1.5 transition-colors ${
                      isSelected
                        ? 'border-brand-500 bg-brand-50/80 text-brand-950 ring-1 ring-brand-400 dark:border-brand-500 dark:bg-brand-950/40 dark:text-brand-100 dark:ring-brand-600'
                        : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`pick-${scope}-${r.rowNo}`}
                      aria-label={`接龙${r.chainNo}选${c.name}`}
                      checked={isSelected}
                      onChange={() => {
                        clearManual(r.rowNo, 'pick');
                        setPicks((prev) => {
                          const next = new Map(prev);
                          next.set(r.rowNo, c);
                          return next;
                        });
                      }}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600 dark:accent-brand-500"
                    />
                    <div className="min-w-0 flex-1 leading-snug">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-slate-900 dark:text-slate-100">{c.name}</span>
                        {isSelected && (
                          <span className="rounded bg-brand-600 px-1.5 py-0.2 text-[10px] text-white">
                            已选
                          </span>
                        )}
                      </div>
                      <span className="block font-mono text-slate-500">{c.phone}</span>
                      <span className="block text-slate-500">{c.address}</span>
                    </div>
                  </label>
                );
              })}
              {picks.has(r.rowNo) && r.candidates.some((c) => c.id === picks.get(r.rowNo)?.id) && (
                <button
                  onClick={() =>
                    setPicks((prev) => {
                      const next = new Map(prev);
                      next.delete(r.rowNo);
                      return next;
                    })
                  }
                  className="text-slate-500 underline"
                >
                  取消选择
                </button>
              )}
              <div className="text-slate-400">都不是的话点下面的「关联联系人」搜,或者「改」自己填</div>
            </div>
          )}
          {usable(r) && (
            <span className="mt-0.5 block text-slate-400">备注:{r.note}</span>
          )}
          {/*
          ★ 「改」对**每一行**都给,不只给下不了的那些。
            用户原话:「能导入的数据也可以修改里面的内容」——
            匹配对了但门牌号差一位的那种,他要能当场改,
            而不是导进去之后再去订单里翻出来改一遍。
        */}
          <button
            onClick={() => setEditing(editing === r.rowNo ? null : r.rowNo)}
            className="mt-1 text-brand-600 underline dark:text-brand-500"
          >
            {editing === r.rowNo ? '收起' : edits.has(r.rowNo) ? '再改' : '改'}
          </button>
          {/*
            ★ 「关联已有的单」—— 用户报的那个情形:
              这个人**其实已经有单了**,但这一行写着「建单」。
              因为自动那条路是按规整后的手机号找的,而表里那个号常常是
              另一种写法、甚至是另一个号(家里人下的单、换过号)——
              于是导完就是同一个人同一天两张单,司机跑两趟同一个地址。

            ★ 关联之后这一行从「建单」变成「加备注」,和自动识别出来的
              那些行走完全同一条路(包括补冻品标记)。
          */}
          <button
            onClick={() => setLinking(linking === r.rowNo ? null : r.rowNo)}
            className="mt-1 ml-3 text-brand-600 underline dark:text-brand-500"
          >
            {links.has(r.rowNo) ? `已关联 ${links.get(r.rowNo)}` : '关联已有的单'}
          </button>
          {links.has(r.rowNo) && (
            <button
              onClick={() =>
                setLinks((prev) => {
                  const next = new Map(prev);
                  next.delete(r.rowNo);
                  return next;
                })
              }
              className="mt-1 ml-2 text-slate-500 underline"
            >
              取消关联
            </button>
          )}
          {linking === r.rowNo && (
            <OrderLinkPicker
              initial={(r.use ?? r.sheet).name || r.buyer}
              target={{
                name: edits.get(r.rowNo)?.name || (r.use ?? r.sheet).name,
                buyer: r.buyer,
                phone: edits.get(r.rowNo)?.phone || (r.use ?? r.sheet).phone,
                address: edits.get(r.rowNo)?.address || (r.use ?? r.sheet).address,
                lat: edits.get(r.rowNo)?.lat ?? r.use?.lat ?? r.matched?.lat ?? null,
                lng: edits.get(r.rowNo)?.lng ?? r.use?.lng ?? r.matched?.lng ?? null,
              }}
              onPick={(orderNo) => {
                clearManual(r.rowNo, 'link');
                /*
                 * ★ 关联了就不再按改过的三件套建单 —— 之前点「改」填的作废。
                 *   不清掉的话界面上还挂着「已手工修改」,人会以为两样都生效了。
                 */
                setEdits((prev) => {
                  if (!prev.has(r.rowNo)) return prev;
                  const next = new Map(prev);
                  next.delete(r.rowNo);
                  return next;
                });
                setLinks((prev) => {
                  const next = new Map(prev);
                  next.set(r.rowNo, orderNo);
                  return next;
                });
                setLinking(null);
              }}
              onClose={() => setLinking(null)}
            />
          )}
          {/*
            ★ 「关联联系人」—— 这个人库里其实有,只是表里写得认不出来
              (电话填错一位、名字写的是微信昵称)。他不想照着联系人再「改」一遍,
              搜出来点一下就用那个联系人的三件套。
          */}
          <button
            onClick={() => setContactLinking(contactLinking === r.rowNo ? null : r.rowNo)}
            className="mt-1 ml-3 text-brand-600 underline dark:text-brand-500"
          >
            {contactLinking === r.rowNo ? '收起' : '关联联系人'}
          </button>
          {picks.has(r.rowNo) && !r.candidates?.some((c) => c.id === picks.get(r.rowNo)?.id) && (
            <button
              onClick={() => dropManual(r.rowNo, 'pick')}
              className="mt-1 ml-2 text-slate-500 underline"
            >
              取消关联联系人
            </button>
          )}
          {/*
            ★ 「关联接龙号」—— 同一个接龙里这个人下了好几单,
              而信息填得不一样,自动没认出来。挑那一单的接龙号,
              这一行就不单独建单,备注加到那一单上。
          */}
          <button
            onClick={() => setMerging(merging === r.rowNo ? null : r.rowNo)}
            className="mt-1 ml-3 text-brand-600 underline dark:text-brand-500"
          >
            {merging === r.rowNo ? '收起' : '关联接龙号'}
          </button>
          {contactLinking === r.rowNo && (
            <div className="mt-1.5">
              <ContactPicker
                inputId={`chain-contact-${scope}-${r.rowNo}`}
                label={`接龙${r.chainNo}关联到哪个联系人`}
                onPick={(c) => {
                  clearManual(r.rowNo, 'pick');
                  setPicks((prev) => {
                    const next = new Map(prev);
                    next.set(r.rowNo, {
                      id: c.id,
                      name: c.name,
                      phone: c.phone,
                      address: c.address,
                      lat: c.lat,
                      lng: c.lng,
                    });
                    return next;
                  });
                  // 关联联系人就是不用自己填了 —— 之前改过的作废,免得两样打架
                  setEdits((prev) => {
                    if (!prev.has(r.rowNo)) return prev;
                    const next = new Map(prev);
                    next.delete(r.rowNo);
                    return next;
                  });
                  setContactLinking(null);
                }}
              />
            </div>
          )}
          {merging === r.rowNo && (
            <div className="mt-1.5 space-y-1">
              <div className="text-slate-500">并到哪一个接龙号的单上?</div>
              {mergeTargets(r).length === 0 ? (
                <div className="text-slate-400">本批没有别的会建单的行</div>
              ) : (
                <select
                  aria-label={`接龙${r.chainNo}关联到哪个接龙号`}
                  defaultValue=""
                  onChange={(e) => {
                    const into = Number(e.target.value);
                    if (!into) return;
                    clearManual(r.rowNo, 'merge');
                    setMerges((prev) => {
                      const next = new Map(prev);
                      next.set(r.rowNo, into);
                      // 别的行原来并在这一行上的,一起跟过去 —— 不然它们挂在一个不建单的行上
                      for (const [k, v] of prev) if (v === r.rowNo) next.set(k, into);
                      return next;
                    });
                    setMerging(null);
                  }}
                  className="h-8 w-full rounded-lg border border-slate-300 bg-white px-2 text-slate-900
                             dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                >
                  <option value="">选接龙号…</option>
                  {mergeTargets(r).map((t) => {
                    const u = picks.get(t.rowNo) ?? t.use ?? t.sheet;
                    return (
                      <option key={t.rowNo} value={t.rowNo}>
                        接龙{t.chainNo} · {t.buyer} · {u.name} {u.phone} · {u.address}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
          )}
          {edits.has(r.rowNo) && (
            <span className="ml-2 text-brand-600 dark:text-brand-500">已手工修改</span>
          )}
        </>
      )}
    </>
  );

  /**
   * 展开之后那块编辑区(三个框 + 地址搜索)。
   * 同样两处共用:表格里另起一行、卡片里跟在下面。
   */
  const editor = (r: PlanRow) => (
    <div className="rounded-xl bg-slate-50 p-2 dark:bg-slate-900/40">
      <div className="grid gap-2 sm:grid-cols-3">
        {(
          [
            ['name', '收件人'],
            ['phone', '电话'],
            ['address', '地址'],
          ] as const
        ).map(([field, label]) => (
          <label key={field} className="block text-xs">
            <span className="mb-0.5 block text-slate-500">{label}</span>
            <input
              aria-label={`接龙${r.chainNo}的${label}`}
              /*
                                          ★ 默认值是**这一行要用的那个值**(匹配到的 > 表里原样),
                                            不是空框 —— 他多半只改其中一项,
                                            空框会逼他把另外两项重新敲一遍。
                                        */
              defaultValue={edits.get(r.rowNo)?.[field] || (r.use ?? r.sheet)[field] || ''}
              onChange={(ev) => {
                const v = ev.target.value;
                setEdits((prev) => {
                  const next = new Map(prev);
                  const cur = { ...(next.get(r.rowNo) ?? {}) };
                  cur[field] = v;
                  next.set(r.rowNo, cur);
                  return next;
                });
              }}
              className="h-9 w-full rounded-lg border border-slate-300 px-2
                                                   text-slate-900 dark:border-slate-600 dark:bg-slate-800
                                                   dark:text-slate-100"
            />
          </label>
        ))}
      </div>
      {/*
                                    ★ 地址要能**当场搜出坐标**。

                                      不搜的话这一单建出来没有坐标:异步地理编码是建完单之后才跑的,
                                      编不出来时只会静静地把单丢进复核队列 —— 司机导航不了、
                                      地图上也看不见。而他此刻正在改这个地址,
                                      是唯一一次能当场拿到坐标的机会。
                                      (用户报的「修改收货人信息的时候地址里面没有联网搜索坐标」。)
                                  */}
      <div className="mt-1.5">
        <AddressSearchBox
          query={edits.get(r.rowNo)?.address ?? (r.use ?? r.sheet).address}
          onPick={(c) =>
            setEdits((prev) => {
              const next = new Map(prev);
              next.set(r.rowNo, {
                ...(next.get(r.rowNo) ?? {}),
                address: c.address,
                lat: c.lat,
                lng: c.lng,
              });
              return next;
            })
          }
        />
        {edits.get(r.rowNo)?.lat != null && (
          <p className="mt-1 text-xs text-green-700 dark:text-green-400">
            已拿到坐标 —— 这一单不用再进地址复核
          </p>
        )}
      </div>
      <p className="mt-1.5 text-xs text-slate-500">
        {/*
                                    ★ 把「改完会怎样」说在他点确认之前。
                                      改过的行一律按**新建一单**走 —— 他改的是收件人三件套,
                                      那意味着这是另一个人 / 另一个地址,
                                      往之前那一单上加备注会把两个人的货写到一张单上。
                                  */}
        改过的行按**新建一单**处理,填什么就用什么(不再去匹配联系人); 电话 0 / 64 / +64
        开头都认。改完还是不能送的(比如写了自提), 会列进失败清单并说明原因。
      </p>
      {edits.has(r.rowNo) && (
        <button
          onClick={() =>
            setEdits((prev) => {
              const next = new Map(prev);
              next.delete(r.rowNo);
              return next;
            })
          }
          className="mt-1 text-xs text-slate-500 underline"
        >
          撤销这一行的修改
        </button>
      )}
    </div>
  );

  async function doConfirm() {
    if (!preview || busy) return;
    setBusy(true);
    setErr(null);
    try {
      setResult(
        await api<Result>(`/imports/chain/${preview.batchId}/confirm`, {
          method: 'POST',
          body: {
            skipRowNos: [...skipped],
            frozenRowNos: [...frozen],
            edits: [...edits.entries()].map(([rowNo, e]) => ({ rowNo, ...e })),
            picks: [...picks.entries()].map(([rowNo, c]) => ({ rowNo, contactId: c.id })),
            links: [...links.entries()].map(([rowNo, orderNo]) => ({ rowNo, orderNo })),
            merges: [...merges.entries()].map(([rowNo, intoRowNo]) => ({ rowNo, intoRowNo })),
          },
        }),
      );
      await onDone();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '导入失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="团购接龙导入"
        className="w-full max-w-4xl max-h-[92dvh] overflow-y-auto rounded-2xl bg-white p-5
                   shadow-2xl dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-baseline justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">团购接龙导入</h3>
          <button onClick={onClose} aria-label="关闭" className="text-xl text-slate-400">
            <InlineIcon icon={X} />
          </button>
        </div>

        {err && (
          <div
            className="mb-3 whitespace-pre-wrap rounded-lg bg-red-50 px-3 py-2 text-sm
                          text-red-700 dark:bg-red-950/40 dark:text-red-300"
          >
            {err}
          </div>
        )}

        {result ? (
          <div className="space-y-3">
            <p className="text-slate-900 dark:text-slate-100">
              建了 {result.created} 单
              {result.appended > 0 && `,另外 ${result.appended} 条加到了已有的单上`}
              {result.skipped > 0 && `,${result.skipped} 条是你手动划掉的`}
              {result.edited > 0 && `,其中 ${result.edited} 条是你手工改过的`}
              {result.picked > 0 && `,${result.picked} 条是你挑了联系人之后救回来的`}
              {result.linked > 0 && `,${result.linked} 条加到了你手动关联的那些单上`}
              {result.merged > 0 && `,${result.merged} 条按你关联的接龙号合并到了同一张单上`}。
            </p>
            {/*
              ★ 失败的要**列出来**,只报一个数字等于没报。
                用户的原话:「最后下单失败的弹出列表提示哪些人没下单,
                列出 接龙号和购买人就行」。
            */}
            {result.failed.length > 0 && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50/60 p-3
                              dark:border-amber-900 dark:bg-amber-950/20"
              >
                <div className="mb-1.5 text-sm text-amber-800 dark:text-amber-300">
                  这 {result.failed.length} 位没下单,要人工处理:
                </div>
                <ul className="space-y-1 text-sm">
                  {result.failed.map((f) => (
                    <li key={`${f.chainNo}-${f.buyer}`} className="flex flex-wrap gap-x-2">
                      <span className="font-mono text-slate-500">接龙{f.chainNo}</span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        {f.buyer || '(没写购买人)'}
                      </span>
                      <span className="text-slate-500">{f.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button
              onClick={onClose}
              className="h-10 w-full rounded-lg bg-brand-600 font-medium text-white"
            >
              完成
            </button>
          </div>
        ) : preview ? (
          <div className="space-y-3">
            <div className="text-sm text-slate-600 dark:text-slate-300">
              标示词 <span className="font-medium">{preview.label}</span> · 配送日{' '}
              <span className="font-medium">{preview.expectedDeliveryDate}</span>
            </div>
            {/*
              ★ 行数对不上必须自己解释掉。
                接龙表一行一个商品,89 行合成 22 单 —— 不说清楚的话
                运营会以为导入漏了一大半。
            */}
            <div className="text-xs text-slate-500">
              表头在第 {preview.headerRowNo} 行;同一单的其他商品行合并了 {preview.mergedRows} 行
              {Object.keys(preview.skippedByStatus).length > 0 &&
                `;按订单状态筛掉 ${Object.entries(preview.skippedByStatus)
                  .map(([k, v]) => `${k} ${v} 行`)
                  .join('、')}`}
            </div>
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="text-slate-700 dark:text-slate-200">
                要建 <b>{preview.counts.create}</b> 单
              </span>
              <span className="text-slate-700 dark:text-slate-200">
                加备注 <b>{preview.counts.appendNote}</b> 条
              </span>
              <span className={preview.counts.fail > 0 ? 'text-amber-700 dark:text-amber-400' : ''}>
                下不了 <b>{preview.counts.fail}</b> 单
              </span>
              {frozen.size > 0 && (
                <span className="text-sky-700 dark:text-sky-400">
                  冻品 <b>{frozen.size}</b> 单
                </span>
              )}
              {skipped.size > 0 && (
                <span className="text-slate-500">
                  手动划掉 <b>{skipped.size}</b> 条
                </span>
              )}
            </div>

            {/*
              ★ 批量选择。二十多行一个一个点勾选框,点到第十行他就不想核了 ——
                而「不想核了」的结果是直接确认,那张表里那几条错的也就导进去了。
                (用户报的:「现在小的框是一个一个需要勾?要给他增加一个批量选择的」。)

              ★ 「全不用」也给:常见的做法是先全不用、再挑出要的那几条 ——
                一场团购里只补发三单的时候,点三下比点十九下快得多。
            */}
            {/*
              ★ 批量操作工具栏：全部用、全不用、全是冻品、都不是冻品。
              带有明确的按钮质感（实体白底、边框、阴影、Hover 状态与点击缩放反馈）。
            */}
            <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50/90 p-2 text-xs dark:border-slate-700 dark:bg-slate-900/40">
              <span className="font-medium text-slate-500 dark:text-slate-400">批量操作:</span>

              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setSkipped(new Set())}
                  className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 shadow-2xs transition-all hover:border-emerald-500 hover:bg-emerald-50/70 hover:text-emerald-800 active:scale-95 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-emerald-600 dark:hover:bg-emerald-950/40 dark:hover:text-emerald-200"
                >
                  <span className="font-bold text-emerald-600 dark:text-emerald-400">✓</span>
                  全部用
                </button>
                <button
                  type="button"
                  onClick={() => setSkipped(new Set(preview.rows.map((r) => r.rowNo)))}
                  className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 shadow-2xs transition-all hover:border-amber-500 hover:bg-amber-50/70 hover:text-amber-800 active:scale-95 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-amber-600 dark:hover:bg-amber-950/40 dark:hover:text-amber-200"
                >
                  <span className="font-bold text-slate-400 dark:text-slate-500"><InlineIcon icon={X} /></span>
                  全不用
                </button>
                {/*
                  ★ 「只用下不了的」—— 第二次导同一张表时用。
                    能导的那些上次已经导过了(再导一遍只会往同一张单上加一条一样的备注,
                    但没必要),他要处理的只是上次没导进去的那几行。
                    原来得先「全不用」、再一行行找出下不了的勾回来。
                */}
                {preview.counts.fail > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setSkipped(
                        new Set(preview.rows.filter((r) => r.action !== 'FAIL').map((r) => r.rowNo)),
                      )
                    }
                    className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 shadow-2xs transition-all hover:border-amber-500 hover:bg-amber-50/70 hover:text-amber-800 active:scale-95 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-amber-600 dark:hover:bg-amber-950/40 dark:hover:text-amber-200"
                  >
                    只用下不了的({preview.counts.fail})
                  </button>
                )}
              </div>

              <div className="h-4 w-px bg-slate-200 dark:bg-slate-700" />

              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setFrozen(new Set(preview.rows.map((r) => r.rowNo)))}
                  className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 shadow-2xs transition-all hover:border-sky-500 hover:bg-sky-50/70 hover:text-sky-800 active:scale-95 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-sky-600 dark:hover:bg-sky-950/40 dark:hover:text-sky-200"
                >
                  <span className="text-sky-500"><InlineIcon icon={Snowflake} /></span>
                  全是冻品
                </button>
                <button
                  type="button"
                  onClick={() => setFrozen(new Set())}
                  className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 shadow-2xs transition-all hover:border-slate-400 hover:bg-slate-100 hover:text-slate-900 active:scale-95 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-slate-500 dark:hover:bg-slate-700"
                >
                  都不是冻品
                </button>
              </div>
            </div>

            {/*
              ★ 表格只在**桌面**显示。七列 860px 宽,手机上要左右划好几次
                才看得到「怎么处理」那一列 —— 而那一列恰恰是他要做决定的地方。
                横滚还会把最右边的列整个藏起来,人根本不知道右边还有东西。
                手机上换成下面那组卡片(和订单列表同一个做法)。
                (用户报的:「这个页面在移动端上需要左右滑动才能看清楚一些东西」。)
            */}
            <div className="hidden overflow-x-auto rounded-xl border border-slate-200 sm:block dark:border-slate-700">
              <table className="w-full min-w-[860px] text-sm">
                <thead className="bg-slate-50 text-slate-600 dark:bg-slate-900/50 dark:text-slate-400">
                  <tr>
                    {/* ★ 勾选列排在最前面:要划掉的人是从左往右扫这张表的 */}
                    <th className="p-2 text-left font-medium">
                      <label className="flex cursor-pointer items-center gap-1.5" title="全选或取消全选">
                        <input
                          type="checkbox"
                          aria-label="用全部或全不用"
                          checked={skipped.size === 0}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate =
                                skipped.size > 0 && skipped.size < preview.rows.length;
                            }
                          }}
                          onChange={(e) => {
                            if (e.target.checked) setSkipped(new Set());
                            else setSkipped(new Set(preview.rows.map((r) => r.rowNo)));
                          }}
                          className="h-4 w-4 rounded accent-brand-600 dark:accent-brand-500"
                        />
                        <span>用</span>
                      </label>
                    </th>
                    <th className="p-2 text-left font-medium">接龙</th>
                    <th className="p-2 text-left font-medium">购买人</th>
                    <th className="p-2 text-left font-medium">表里写的</th>
                    <th className="p-2 text-left font-medium">要用的</th>
                    <th className="p-2 text-left font-medium">
                      <label className="flex cursor-pointer items-center gap-1.5" title="全是冻品或全不是冻品">
                        <input
                          type="checkbox"
                          aria-label="全是冻品或都不是冻品"
                          checked={
                            preview.rows.filter(usable).length > 0 &&
                            preview.rows.filter(usable).every((r) => frozen.has(r.rowNo))
                          }
                          ref={(el) => {
                            if (el) {
                              const active = preview.rows.filter(usable);
                              const frozenCount = active.filter((r) => frozen.has(r.rowNo)).length;
                              el.indeterminate = frozenCount > 0 && frozenCount < active.length;
                            }
                          }}
                          onChange={(e) => {
                            if (e.target.checked) setFrozen(new Set(preview.rows.map((r) => r.rowNo)));
                            else setFrozen(new Set());
                          }}
                          className="h-4 w-4 rounded accent-brand-600 dark:accent-brand-500"
                        />
                        <span>冻品</span>
                      </label>
                    </th>
                    <th className="p-2 text-left font-medium">怎么处理</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <Fragment key={`${r.chainNo}-${r.buyer}`}>
                      <tr
                        className={`border-t border-slate-100 align-top dark:border-slate-700/50 ${
                          skipped.has(r.rowNo) ? 'opacity-40' : ''
                        }`}
                      >
                        <td className="p-2">
                          {/*
                          ★ 「下不了」的行**也给**勾选框。
                            原来只有改好了才出现 —— 而点了「全不用」之后,划掉的行
                            连「改」都藏起来了,这几行就再也拿不回来。
                            第二次导同一张表时恰恰是这样用的:先全不用,
                            再把上次没导进去的那几行一个个勾上、改好。
                            (勾上但没改好的,确认时照旧进失败清单并写明原因。)
                        */}
                          <input
                            type="checkbox"
                            aria-label={`用接龙${r.chainNo}`}
                            checked={!skipped.has(r.rowNo)}
                            onChange={(e) =>
                              setSkipped((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.delete(r.rowNo);
                                else next.add(r.rowNo);
                                return next;
                              })
                            }
                          />
                        </td>
                        <td className="p-2 font-mono text-slate-500">{r.chainNo}</td>
                        <td className="p-2">
                          <div className="text-slate-900 dark:text-slate-100">{r.buyer}</div>
                          <div className="text-xs text-slate-400">{r.totalAmount}</div>
                        </td>
                        <td className="p-2 text-xs text-slate-500">
                          <div>{r.sheet.name || '—'}</div>
                          <div className="font-mono">{r.sheet.phone || '—'}</div>
                          <div>{r.sheet.address || '—'}</div>
                        </td>
                        <td className="p-2 text-xs">
                          {(() => {
                            const picked = picks.get(r.rowNo) ?? null;
                            const effectiveUse = edits.has(r.rowNo)
                              ? {
                                  name: edits.get(r.rowNo)?.name || (r.use ?? r.sheet).name,
                                  phone: edits.get(r.rowNo)?.phone || (r.use ?? r.sheet).phone,
                                  address: edits.get(r.rowNo)?.address || (r.use ?? r.sheet).address,
                                }
                              : picked
                                ? { name: picked.name, phone: picked.phone, address: picked.address }
                                : r.use;

                            if (!effectiveUse) return <span className="text-slate-400">—</span>;

                            return (
                              <>
                                <div className="text-slate-900 dark:text-slate-100">{effectiveUse.name}</div>
                                <div className="font-mono text-slate-500">{effectiveUse.phone}</div>
                                <div className="text-slate-500">{effectiveUse.address}</div>
                                {picked ? (
                                  <div className="mt-0.5 font-medium text-brand-600 dark:text-brand-500">
                                    用了已选联系人「{picked.name}」
                                  </div>
                                ) : r.matchedBy ? (
                                  <div className="mt-0.5 text-brand-600 dark:text-brand-500">
                                    用了联系人「{r.matched?.name}」({MATCH_LABEL[r.matchedBy]})
                                  </div>
                                ) : null}
                              </>
                            );
                          })()}
                        </td>
                        <td className="p-2">
                          {/*
                          ★ 只要会建单（原可建、或已挑联系人、或已改地址），就给冻品勾选框
                        */}
                          {usable(r) && (
                            <input
                              type="checkbox"
                              aria-label={`接龙${r.chainNo}是冻品`}
                              checked={frozen.has(r.rowNo)}
                              onChange={(e) =>
                                setFrozen((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(r.rowNo);
                                  else next.delete(r.rowNo);
                                  return next;
                                })
                              }
                            />
                          )}
                        </td>
                        <td className="p-2 text-xs">{decision(r, 'desktop')}</td>
                      </tr>
                      {/*
                      ★ 编辑区占**整行**另起一行,不挤在单元格里。
                        三个输入框塞进那一格的话每个只有四五十像素宽,
                        而他要填的是一整条地址。
                    */}
                      {editing === r.rowNo && (
                        <tr className="bg-slate-50 dark:bg-slate-900/40">
                          <td />
                          <td colSpan={6} className="p-2">
                            {editor(r)}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/*
              ★ 手机上换成卡片,不横滚。
                七列 860px 宽的表格在手机上要左右划好几次才看得到
                「怎么处理」那一列 —— 而那一列正是他要做决定的地方;
                横滚还会把最右边的列整个藏起来,人根本不知道右边还有东西。
                (订单列表早就是这么做的,这里跟同一个做法。)

              ★ 卡片和表格共用 `decision()` / `editor()` 两个函数,
                不各写一份 —— 各写一份的话以后加一个按钮必然只加到一边,
                而漏掉的那一边是手机:没人会在手机上点着检查。
            */}
            <ul aria-label="接龙核对卡片" className="space-y-2 sm:hidden">
              {preview.rows.map((r) => (
                <li
                  key={`card-${r.chainNo}-${r.buyer}`}
                  className={`rounded-xl border border-slate-200 p-3 dark:border-slate-700 ${
                    skipped.has(r.rowNo) ? 'opacity-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm text-slate-900 dark:text-slate-100">
                        <span className="font-mono text-slate-500">接龙{r.chainNo}</span>
                        <span className="ml-2">{r.buyer}</span>
                        <span className="ml-2 text-slate-400">{r.totalAmount}</span>
                      </div>
                      {/*
                        ★ 「表里写的」和「要用的」在手机上**上下排**,不并排。
                          并排的话两列各剩一半屏宽,一条地址折成四行,
                          而他要做的事恰恰是把两者对着看。
                      */}
                      <div className="mt-1 text-xs text-slate-500">
                        表里:{r.sheet.name || '—'} · {r.sheet.phone || '—'}
                        <span className="block">{r.sheet.address || '—'}</span>
                      </div>
                      {(() => {
                        const picked = picks.get(r.rowNo) ?? null;
                        const effectiveUse = edits.has(r.rowNo)
                          ? {
                              name: edits.get(r.rowNo)?.name || (r.use ?? r.sheet).name,
                              phone: edits.get(r.rowNo)?.phone || (r.use ?? r.sheet).phone,
                              address: edits.get(r.rowNo)?.address || (r.use ?? r.sheet).address,
                            }
                          : picked
                            ? { name: picked.name, phone: picked.phone, address: picked.address }
                            : r.use;

                        if (!effectiveUse) return null;
                        return (
                          <div className="mt-1 text-xs">
                            <span className="text-slate-400">要用:</span>
                            <span className="text-slate-900 dark:text-slate-100">{effectiveUse.name}</span>
                            <span className="ml-1 font-mono text-slate-500">{effectiveUse.phone}</span>
                            <span className="block text-slate-500">{effectiveUse.address}</span>
                            {picked ? (
                              <span className="font-medium text-brand-600 dark:text-brand-500">
                                用了已选联系人「{picked.name}」
                              </span>
                            ) : r.matchedBy ? (
                              <span className="text-brand-600 dark:text-brand-500">
                                用了联系人「{r.matched?.name}」({MATCH_LABEL[r.matchedBy]})
                              </span>
                            ) : null}
                          </div>
                        );
                      })()}
                    </div>
                    {/* 两个勾选框竖着放右边,手指够得着(不是表格里那种 16px 小框) */}
                    <div className="flex shrink-0 flex-col items-center gap-2 text-xs">
                      {/* 「用」每一行都给,理由见表格那一列 */}
                      <label className="flex flex-col items-center gap-0.5">
                        <input
                          type="checkbox"
                          aria-label={`用接龙${r.chainNo}`}
                          className="h-5 w-5"
                          checked={!skipped.has(r.rowNo)}
                          onChange={(e) =>
                            setSkipped((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.delete(r.rowNo);
                              else next.add(r.rowNo);
                              return next;
                            })
                          }
                        />
                        <span className="text-slate-500">用</span>
                      </label>
                      {usable(r) && (
                        <label className="flex flex-col items-center gap-0.5">
                          <input
                            type="checkbox"
                            aria-label={`接龙${r.chainNo}是冻品`}
                            className="h-5 w-5"
                            checked={frozen.has(r.rowNo)}
                            onChange={(e) =>
                              setFrozen((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(r.rowNo);
                                else next.delete(r.rowNo);
                                return next;
                              })
                            }
                          />
                          <span className="text-slate-500">冻品</span>
                        </label>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 border-t border-slate-100 pt-2 text-xs dark:border-slate-700/50">
                    {decision(r, 'mobile')}
                  </div>
                  {editing === r.rowNo && <div className="mt-2">{editor(r)}</div>}
                </li>
              ))}
            </ul>

            <div className="flex gap-3">
              <button
                onClick={() => setPreview(null)}
                className="h-10 flex-1 rounded-lg border border-slate-300 text-slate-700
                           dark:border-slate-600 dark:text-slate-300"
              >
                重新选文件
              </button>
              <button
                onClick={() => void doConfirm()}
                /*
                 * ★ 人改过的行要算进「会建单」里。
                 *
                 *   不算的话:一张表只有一行、而那行本来是「下不了」,
                 *   他在页面上把地址补全了,**确认按钮还是灰的** ——
                 *   点不下去,而界面没有任何一句话解释为什么。
                 *   (这个洞是写界面用例时撞上的:改完之后点不动。)
                 */
                disabled={busy || willCreate + willAppend === 0}
                className="h-10 flex-1 rounded-lg bg-brand-600 font-medium text-white
                           disabled:opacity-40"
              >
                {busy ? '导入中…' : `确认导入(${willCreate} 单)`}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-sm text-slate-600 dark:text-slate-400">
                接龙导出的 Excel
              </span>
              <input
                aria-label="接龙文件"
                type="file"
                accept=".xlsx,.csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="w-full text-sm"
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-sm text-slate-600 dark:text-slate-400">
                标示词 —— 用来区分是哪一场团购
              </span>
              <input
                aria-label="标示词"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="例如 牛板筋团"
                className={INPUT}
              />
              {/*
                ★ 说清楚它会出现在哪里。这个词会跟着备注一路走到司机手机上
                  和小票上,而填的时候人看不到那一步。
              */}
              <span className="mt-1 block text-xs text-slate-500">
                会和接龙号、订单总金额一起写进订单备注,例如「
                {(label.trim() || '标示词') + ' 接龙3 125.00'}」
              </span>
            </label>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-sm text-slate-600 dark:text-slate-400">
                  配送日期<span className="text-slate-400"> —— 留空按今天</span>
                </span>
                <input
                  aria-label="配送日期"
                  type="date"
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  className={INPUT}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm text-slate-600 dark:text-slate-400">
                  取货点
                </span>
                <select
                  aria-label="取货点"
                  value={pointId}
                  onChange={(e) => {
                    pointTouched.current = true;
                    setPointId(e.target.value);
                  }}
                  className={INPUT}
                >
                  <option value="">不指定</option>
                  {points.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/*
              ★ 「这一批全是冻品」放在这里,而不是核对页上。
                它只是给核对页定个**默认值** —— 团购最常见的情形就是
                整批都是冻品,勾一次省掉几十次点击;
                而一批里只有一部分是冻品的时候,就别勾这个,
                到核对页上一行一行点。
            */}
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                aria-label="这一批全是冻品"
                className="mt-1"
                checked={allFrozen}
                onChange={(e) => setAllFrozen(e.target.checked)}
              />
              <span>
                <span className="text-slate-800 dark:text-slate-100">这一批全是冻品</span>
                <span className="block text-xs text-slate-500">
                  下一步每一行会默认勾上「冻品」,可以逐行取消。
                  只有一部分是冻品的话别勾这个,到下一步单独点。
                </span>
              </span>
            </label>

            <p className="text-xs text-slate-500">
              只导「订单进行中」的单;同一单的多个商品行会合并成一单。 表里写歪的收货人 / 电话 /
              地址会先拿库里的联系人纠正一遍 —— 下一步给你逐行核对。
            </p>

            <button
              onClick={() => void doPreview()}
              disabled={busy || !file || !label.trim()}
              className="h-10 w-full rounded-lg bg-brand-600 font-medium text-white disabled:opacity-40"
            >
              {busy ? '读取中…' : !file ? '先选文件' : !label.trim() ? '先填标示词' : '下一步:核对'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const INPUT =
  'w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 ' +
  'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm';
